/**
 * Seeded deterministic PRNG (mulberry32).
 *
 * Used by the battle engine and encounter system. Same seed + same offset
 * MUST produce the same outputs across platforms — this is the foundation
 * of replay determinism.
 */

export interface SeededRng {
  readonly seed: number;
  /** Returns the current internal counter (number of draws so far). */
  readonly offset: number;
  /** Float in [0, 1). */
  next(): number;
  /** Integer in [min, max] inclusive. */
  nextInt(min: number, max: number): number;
  /** Returns true with probability p (0..1). */
  chance(p: number): boolean;
  /** Pick a random element from a non-empty array. */
  pick<T>(items: readonly T[]): T;
  /** Snapshot for serialization (saves include this). */
  snapshot(): RngSnapshot;
}

export interface RngSnapshot {
  seed: number;
  offset: number;
}

const STEP = 0x6d2b79f5;
const U32 = 4_294_967_296;

function fastForwardState(seed: number, offset: number): number {
  let state = seed >>> 0;
  for (let i = 0; i < offset; i += 1) {
    state = (state + STEP) >>> 0;
  }
  return state;
}

export function createRng(seed: number, offset = 0): SeededRng {
  let state = fastForwardState(seed, offset);
  let counter = offset;

  const next = (): number => {
    state = (state + STEP) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    counter += 1;
    return ((t ^ (t >>> 14)) >>> 0) / U32;
  };

  return {
    get seed() {
      return seed;
    },
    get offset() {
      return counter;
    },
    next,
    nextInt(min, max) {
      if (max < min) throw new Error('nextInt: max < min');
      return Math.floor(next() * (max - min + 1)) + min;
    },
    chance(p) {
      return next() < p;
    },
    pick<T>(items: readonly T[]): T {
      if (items.length === 0) throw new Error('pick: empty array');
      const item = items[Math.floor(next() * items.length)];
      if (item === undefined) throw new Error('pick: undefined slot');
      return item;
    },
    snapshot() {
      return { seed, offset: counter };
    },
  };
}

export function restoreRng(snap: RngSnapshot): SeededRng {
  return createRng(snap.seed, snap.offset);
}
